<template>
  <div ref="componentEl">
    <aw-section-wrapper :has-padding="!isCatalogFooterVariant">
      <template #content="{ contentEvents: { contentLoading } }">
        <client-only>
          <aw-tab-carousel
            :empty-placeholder="$awt('aw.cms.catalog.list.fallback_slide.text')"
            :main-title="mainTitleOverride || (needTitle ? $awt('aw.cms.catalog.list.main_title') : '')"
            tab-key="id"
            slide-key="id"
            :tabs="tabs"
            :breakpoints="breakpoints"
            :slides="slides"
            :is-carousel="isCarousel"
            :class="$style.tabCarousel"
            :has-title-padding="hasTitlePadding"
            @tab-opened="getCatalog(contentLoading, $event.newTabItem)"
          >
            <template #slide(all)(all)="{ slide }">
              <div
                :data-banner-id="slide.id"
                :data-banner-index="getGlobalBannerIndex(slide)"
              >
                <aw-catalog-card
                  :key="slide.id"
                  :catalog="slide"
                  :analytics-measurable="analyticsMeasurable"
                  :analytics-banner-interaction="extendAnalyticsBannerInteraction(slide)"
                />
              </div>
            </template>
          </aw-tab-carousel>
          <div
            v-if="!hideAllButton"
            :data-banner-id="ALL_BUTTON_BANNER_ID"
            :data-banner-index="0"
            :class="$style.articleButton"
          >
            <lv-button
              styling="secondary-dark"
              shrink
              :href="$awt('aw.cms.catalog.list.show_all_catalogs.href')"
              v-on="clickEventHandlers"
            >
              {{ $awt('aw.cms.catalog.list.show_all_catalogs.label') }}
            </lv-button>
          </div>
        </client-only>
      </template>
    </aw-section-wrapper>
  </div>
</template>

<script setup>
  import { computed, defineAsyncComponent, inject, nextTick, reactive, ref, toRef } from 'vue';
  import { useNuxtApp } from 'nuxt/app';
  import { AW_AX_CACHE_GLOBAL } from '~~/common/plugins/axios/awAxCache';
  import { useUserInterfaceStore } from '~~/common/stores/userInterface.js';
  import otqs from '~~/common/utils/objectToQueryString';
  import { genBannerInteractionClickEventHandlers } from '~~/common/utils/bannerInteractionAnalytics';
  import { LvButton } from '~~/common/components/loginet-vue-shop/index';
  import { AnalyticsBannerInteraction, EMPTY_ANALYTICS_BANNER_INTERACTION } from '~~/common/utils/AnalyticsBannerInteraction';
  import { useBannerInteractionAnalytics } from '~~/common/composables/useBannerInteractionAnalytics';

  const AwCatalogCard = defineAsyncComponent(() => import('~~/shop/app/components/Page/Catalog/AwCatalogCard'));
  const AwTabCarousel = defineAsyncComponent(() => import('~~/common/components/AwTabCarousel'));
  const AwSectionWrapper = defineAsyncComponent(() => import('~~/common/components/Page/Common/AwSectionWrapper'));

  const props = defineProps({
    analyticsBannerInteraction: {
      type: [Object, Symbol],
      default: null,
      validator: !AnalyticsBannerInteraction || ((val) => {
        return (val === EMPTY_ANALYTICS_BANNER_INTERACTION) || AnalyticsBannerInteraction.isValid(val);
      }),
    },
    analyticsMeasurable: {
      type: Boolean,
      default: false,
    },
    isCarousel: {
      type: Boolean,
      default: true,
    },
    store: {
      type: Object,
      default: null,
    },
    needTitle: {
      type: Boolean,
      default: false,
    },
    breakpointsOverwrite: {
      type: Array,
      default: () => ([]),
    },
    hasTitlePadding: {
      type: Boolean,
      default: true,
    },
    hideAllButton: {
      type: Boolean,
      default: false,
    },
    mainTitleOverride: {
      type: String,
      default: null,
    },
  });

  const emit = defineEmits(['is-empty']);

  const {
    $awt,
    $api,
  } = useNuxtApp();
  const userInterfaceStore = useUserInterfaceStore();
  const screenRange = computed(() => userInterfaceStore.mediaQueries);

  const tabs = [
    {
      id: 'current',
      tabTitle: '',
    },
  ];
  const isCatalogFooterVariant = inject('catalogFooterVariant', false);

  const ALL_BUTTON_BANNER_ID = 'show-all-catalogs';

  const slides = reactive(Object.fromEntries(tabs.map(e => [e?.id, []])));
  const componentEl = ref(null);

  const ALL_BUTTON_PROMOTION_BANNER_INDEX = $awt('aw.cms.catalog.list.show_all_catalogs.promotion_banner_index');
  const ALL_BUTTON_PROMOTION_NAME = $awt('aw.cms.catalog.list.show_all_catalogs.promotion_name');

  const bannersRef = computed(() => {
    const allSlides = Object.values(slides).flat();
    const catalogBanners = allSlides.map(catalog => ({
      ...catalog,
      campaignName: catalog.title || null,
    }));
    return [
      ...catalogBanners,
      {
        id: ALL_BUTTON_BANNER_ID,
        campaignName: ALL_BUTTON_PROMOTION_NAME,
        promotionBannerIndex: ALL_BUTTON_PROMOTION_BANNER_INDEX,
      },
    ];
  });

  const {
    getGlobalBannerIndex,
    extendAnalyticsBannerInteraction,
    observeBannerCards,
  } = useBannerInteractionAnalytics({
    componentEl,
    banners: bannersRef,
    analyticsBannerInteraction: toRef(props, 'analyticsBannerInteraction'),
    analyticsMeasurable: toRef(props, 'analyticsMeasurable'),
  });

  const breakpoints = computed(() => {
    return props.breakpointsOverwrite.length ? props.breakpointsOverwrite : [
      {
        at: screenRange.value['desktop-medium-min'],
        count: 4,
      },
      {
        at: screenRange.value['tablet-min'],
        count: 3,
      },
      {
        at: true,
        count: 'auto',
      },
    ];
  });

  const clickEventHandlers = computed(() => {
    return genBannerInteractionClickEventHandlers({
      analyticsBannerInteraction: AnalyticsBannerInteraction.create({
        ...props.analyticsBannerInteraction,
        promotionBannerIndex: ALL_BUTTON_PROMOTION_BANNER_INDEX,
        promotionName: ALL_BUTTON_PROMOTION_NAME,
      }),
      analyticsMeasurable: props.analyticsMeasurable,
    });
  });

  async function getCatalog (updateLoadingState, { id }) {
    updateLoadingState(true);

    let params = {
      availability: id,
    };

    if (props.store?.id) {
      params = {
        ...params,
        storeCode: props.store.id,
      };
    }

    slides[id] = (await $api.$get(`catalog/list${otqs(params)}`, { awAxCache: AW_AX_CACHE_GLOBAL }));
    updateLoadingState(false);
    emit('is-empty', slides[id].length === 0);
    nextTick(() => {
      nextTick(() => {
        observeBannerCards();
      });
    });
  }
</script>
<style module lang="scss" rel="stylesheet/scss">
.tabCarousel {
  --awTabCarouselParentMinHeight: 312px;
  --awTabCarouselMinHeight: 312px;
}

.article {
  &Button {
    display: flex;
    justify-content: center;
    margin-top: 20px;

    @include tablet(min) {
      margin-top: 40px;
    }
  }
}
</style>
